import { FC, useMemo } from "react";
import { createPluginRegistration } from "@embedpdf/core";
import { EmbedPDF } from "@embedpdf/core/react";
import { usePdfiumEngine } from "@embedpdf/engines/react";
import {
  DocumentContent,
  DocumentManagerPluginPackage,
} from "@embedpdf/plugin-document-manager/react";
import {
  RenderLayer,
  RenderPluginPackage,
} from "@embedpdf/plugin-render/react";
import { Scroller, ScrollPluginPackage } from "@embedpdf/plugin-scroll/react";
import {
  Viewport,
  ViewportPluginPackage,
} from "@embedpdf/plugin-viewport/react";
import {
  ZoomGestureWrapper,
  ZoomMode,
  ZoomPluginPackage,
} from "@embedpdf/plugin-zoom/react";

import { VnBox } from "@/components/VnBox.tsx";
import { VnPdfToolbar } from "@/components/VnPdfViewer/VnPdfToolbar.tsx";
import { VnText } from "@/components/VnText/VnText.tsx";
import { cn } from "@/utils/tailwind.ts";

export type VnPdfViewerPropsType = {
  className?: string;
  pdfUrl: string;
};

export const VnPdfViewer: FC<VnPdfViewerPropsType> = ({
  className,
  pdfUrl,
}) => {
  const { engine, isLoading } = usePdfiumEngine();

  const plugins = useMemo(
    () => [
      createPluginRegistration(DocumentManagerPluginPackage, {
        initialDocuments: [{ url: pdfUrl }],
      }),
      createPluginRegistration(ScrollPluginPackage),
      createPluginRegistration(ViewportPluginPackage),
      createPluginRegistration(RenderPluginPackage),
      createPluginRegistration(ZoomPluginPackage, {
        defaultZoomLevel: ZoomMode.FitWidth,
      }),
    ],
    [pdfUrl],
  );

  if (isLoading || !engine) {
    return <VnText i18nKey="common:pdfViewer.loading" />;
  }

  return (
    <EmbedPDF engine={engine} plugins={plugins}>
      {({ activeDocumentId }) =>
        activeDocumentId && (
          <DocumentContent documentId={activeDocumentId}>
            {({ isLoaded }) =>
              isLoaded && (
                <VnBox
                  className={cn(
                    "border-border flex h-full flex-col border",
                    className,
                  )}>
                  <VnPdfToolbar documentId={activeDocumentId} />
                  <Viewport
                    documentId={activeDocumentId}
                    className="bg-secondary">
                    <ZoomGestureWrapper documentId={activeDocumentId}>
                      <Scroller
                        documentId={activeDocumentId}
                        renderPage={({ width, height, pageIndex }) => (
                          <div style={{ width, height }}>
                            <RenderLayer
                              documentId={activeDocumentId}
                              pageIndex={pageIndex}
                            />
                          </div>
                        )}
                      />
                    </ZoomGestureWrapper>
                  </Viewport>
                </VnBox>
              )
            }
          </DocumentContent>
        )
      }
    </EmbedPDF>
  );
};
